import { useSyncExternalStore } from "react";

export type DownloadFractions = ReadonlyMap<string, number>;

const emptyFractions: DownloadFractions = new Map();

// Live per-video download progress (0..1), deliberately OUTSIDE mobileVideoStore.
//
// Progress ticks arrive up to ~10×/second per active download. If they flowed
// through mobileVideoStore, every commit would hand a new state object to
// EVERY subscriber — bookmarks, playlist, the whole funder workspace — and
// re-render them, even though none of them care about a download fraction.
// That is what froze the Round Overview (a 2,700-line panel re-rendering ~10×/s
// during a download). Keeping progress here means it is structurally
// impossible for a non-progress consumer to re-render on a tick: the durable
// store never commits for progress, and only the two download-progress UIs
// (DownloadAllControl, DownloadDetails) subscribe to this channel.
//
// In-memory only: progress is ephemeral and reconstructed on reconcile, so it
// never needs to persist.
class DownloadProgressStore {
  // Replaced, never mutated, so an aggregate consumer (the "download all" bar)
  // gets a new snapshot on every change and passes it as a real input. Under
  // the React Compiler a side-channel read of the store is memoized away.
  private fractions: DownloadFractions = new Map();
  private readonly listeners = new Set<() => void>();

  get(id: string): number {
    return this.fractions.get(id) ?? 0;
  }

  getSnapshot(): DownloadFractions {
    return this.fractions;
  }

  set(id: string, value: number): void {
    if (this.fractions.get(id) === value) {
      return;
    }

    this.commit((next) => {
      next.set(id, value);
    });
  }

  // Zero a whole handoff's fractions with ONE notification. Per-id `set` calls
  // would wake the progress UIs once per video, and each wake recomputes the
  // round's byte totals across every video — quadratic work in the same frame
  // as the handoff that queued them.
  reset(ids: readonly string[], options: { remove?: boolean } = {}): void {
    const changedIds = ids.filter((id) =>
      options.remove === true
        ? this.fractions.has(id)
        : this.fractions.get(id) !== 0,
    );

    if (changedIds.length === 0) {
      return;
    }

    this.commit((next) => {
      for (const id of changedIds) {
        if (options.remove === true) {
          next.delete(id);
        } else {
          next.set(id, 0);
        }
      }
    });
  }

  clear(id: string): void {
    if (!this.fractions.has(id)) {
      return;
    }

    this.commit((next) => {
      next.delete(id);
    });
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  private commit(change: (next: Map<string, number>) => void): void {
    const next = new Map(this.fractions);
    change(next);
    this.fractions = next;
    this.emit();
  }

  private emit(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}

export const downloadProgressStore = new DownloadProgressStore();

// Per-video progress. The snapshot is the video's own fraction, so a component
// using this only re-renders when THAT video's progress changes — a tick for a
// different video is a no-op (useSyncExternalStore bails on an unchanged
// number).
export function useDownloadProgress(videoId: string): number {
  return useSyncExternalStore(
    (listener) => downloadProgressStore.subscribe(listener),
    () => downloadProgressStore.get(videoId),
    () => 0,
  );
}

// Every video's progress at once, for an aggregate consumer (the "download
// all" byte bar). Re-renders on any progress change.
export function useDownloadFractions(): DownloadFractions {
  return useSyncExternalStore(
    (listener) => downloadProgressStore.subscribe(listener),
    () => downloadProgressStore.getSnapshot(),
    () => emptyFractions,
  );
}
